/**
 * Mock quotes — at least 2 quotes per non-open request, with the dev provider
 * sending quotes to several requests so its dashboard shows pipeline activity.
 */

import type { Tables, Json } from '@/lib/db.types'
import { quoteEntityId, requestEntityId, providerId } from './ids'
import { DEV_PROVIDER } from './providers'

type Quote = Tables<'quotes'>

const NOW = Date.parse('2026-05-01T08:00:00.000Z')
const T = (days: number) => new Date(NOW + days * 86400_000).toISOString()

interface QuoteSeed {
  requestN: number
  providerEntityId: string
  price: number
  price_type: Quote['price_type']
  status: Quote['status']
  message: string
  ai_drafted: boolean
  daysAgo: number
}

const SEEDS: QuoteSeed[] = [
  // Request 2 — bohagsflytt (quoted) — 3 quotes incoming
  { requestN: 2, providerEntityId: providerId(1),  price: 9500,  price_type: 'fixed', status: 'pending',
    message: 'Hej Sigge! Vi tar oss an flytten på en dag. Pris inkluderar två flyttgubbar och bilen.',
    ai_drafted: false, daysAgo: 4 },
  { requestN: 2, providerEntityId: providerId(11), price: 8800,  price_type: 'fixed', status: 'pending',
    message: 'Vi kan hjälpa dig. Försäkrade, F-skatt och vana flyttare. Vi tar med oss flyttkartonger.',
    ai_drafted: false, daysAgo: 3 },
  { requestN: 2, providerEntityId: providerId(15), price: 10500, price_type: 'fixed', status: 'pending',
    message: 'Inkluderar bohagsförsäkring upp till 200 000 kr. Två flyttbilar, fyra montörer på plats.',
    ai_drafted: false, daysAgo: 2 },

  // Request 3 — elektriker (booked) — 2 quotes, one accepted
  { requestN: 3, providerEntityId: providerId(3),  price: 2400,  price_type: 'fixed', status: 'accepted',
    message: 'Hej! Jag kan komma onsdag eftermiddag. Pris inkluderar dragning samt installation.',
    ai_drafted: false, daysAgo: 11 },
  { requestN: 3, providerEntityId: providerId(13), price: 3100,  price_type: 'fixed', status: 'declined',
    message: 'Vi kan göra det i nästa vecka. Pris inkluderar besök och installation.',
    ai_drafted: false, daysAgo: 10 },

  // Request 4 — mobelmontering (completed) — 2 quotes
  { requestN: 4, providerEntityId: providerId(15), price: 1900,  price_type: 'fixed', status: 'accepted',
    message: 'Vi monterar dina PAX-garderober på under 3 timmar. Kommer på torsdag.',
    ai_drafted: false, daysAgo: 27 },
  { requestN: 4, providerEntityId: DEV_PROVIDER.id, price: 2100, price_type: 'fixed', status: 'declined',
    message: 'Hej! Jag kan komma redan imorgon om det passar bättre. Kvalitetsmontering med garanti.',
    ai_drafted: true, daysAgo: 27 },

  // Request 5 — målare (completed)
  { requestN: 5, providerEntityId: providerId(6),  price: 8500,  price_type: 'fixed', status: 'accepted',
    message: 'Inkluderar två lager täckfärg och nödvändigt material. Färdigt på två arbetsdagar.',
    ai_drafted: false, daysAgo: 58 },

  // Request 8 — trädfällning (quoted)
  { requestN: 8, providerEntityId: providerId(7),  price: 7200,  price_type: 'fixed', status: 'pending',
    message: 'Inkluderar fällning, kapning och bortkörning av materialet. Stubbfräsning enligt önskemål.',
    ai_drafted: false, daysAgo: 5 },
  { requestN: 8, providerEntityId: providerId(14), price: 6900,  price_type: 'fixed', status: 'pending',
    message: 'Vi tar med oss avancerad utrustning. Kan komma redan nästa vecka.',
    ai_drafted: false, daysAgo: 4 },

  // Request 10 — kakelsättare (booked)
  { requestN: 10, providerEntityId: providerId(17), price: 24500, price_type: 'fixed', status: 'accepted',
    message: 'Vi tar med oss allt material. Klart inom 5 arbetsdagar. ROT-avdrag direkt.',
    ai_drafted: false, daysAgo: 13 },
  { requestN: 10, providerEntityId: providerId(12), price: 27000, price_type: 'fixed', status: 'declined',
    message: 'Premiumkakel ingår. 5 års garanti på allt arbete.',
    ai_drafted: false, daysAgo: 12 },

  // Request 11 — datorhjälp (completed)
  { requestN: 11, providerEntityId: providerId(9),  price: 890,   price_type: 'fixed', status: 'accepted',
    message: 'Kan hjälpa via fjärrsupport idag. Felsökning + säker uppdatering ingår.',
    ai_drafted: false, daysAgo: 18 },

  // Request 12 — gräsklippning (quoted)
  { requestN: 12, providerEntityId: providerId(7),  price: 5800,  price_type: 'fixed', status: 'pending',
    message: 'Säsongsavtal maj–september, varannan vecka. Material och bortforsling ingår.',
    ai_drafted: false, daysAgo: 8 },
  { requestN: 12, providerEntityId: providerId(14), price: 6400,  price_type: 'fixed', status: 'pending',
    message: 'Vi sköter trädgården åt dig hela säsongen. Inkluderar även kantskärning.',
    ai_drafted: false, daysAgo: 7 },

  // Request 14 — matteundervisning (completed)
  { requestN: 14, providerEntityId: providerId(10), price: 3200,  price_type: 'fixed', status: 'accepted',
    message: 'Vi kör 8 lektioner à 60 min. Hembesök eller online — du väljer.',
    ai_drafted: false, daysAgo: 39 },

  // Dev provider sends a quote on a fresh open request (active pipeline indicator)
  { requestN: 9, providerEntityId: DEV_PROVIDER.id, price: 11500, price_type: 'fixed', status: 'pending',
    message: 'Hej! Jag bygger gärna en specialdesignad TV-bänk i ek. Tar 4 arbetsdagar inkl. lackning.',
    ai_drafted: false, daysAgo: 2 },
]

export const MOCK_QUOTES: Quote[] = SEEDS.map((s, i) => {
  const n = i + 1
  const createdAt = new Date(NOW - s.daysAgo * 86400_000).toISOString()
  return {
    id: quoteEntityId(n),
    request_id: requestEntityId(s.requestN),
    provider_id: s.providerEntityId,
    price: s.price,
    price_type: s.price_type,
    message: s.message,
    available_slots: [
      { date: T(s.daysAgo > 10 ? -s.daysAgo + 5 : 7), time: '09:00' },
      { date: T(s.daysAgo > 10 ? -s.daysAgo + 6 : 10), time: '13:00' },
    ] as unknown as Json,
    valid_until: T(s.daysAgo > 10 ? -s.daysAgo + 14 : 14),
    status: s.status,
    ai_drafted: s.ai_drafted,
    created_at: createdAt,
    updated_at: createdAt,
  } satisfies Quote
})

export function findQuotesByRequestId(reqId: string): Quote[] {
  return MOCK_QUOTES.filter((q) => q.request_id === reqId)
}

export function findQuoteById(id: string): Quote | undefined {
  return MOCK_QUOTES.find((q) => q.id === id)
}

export function findQuotesByProviderId(providerEntityId: string): Quote[] {
  return MOCK_QUOTES.filter((q) => q.provider_id === providerEntityId)
}
